import React, { useCallback, useMemo, useState, useContext } from 'react'
import useDaycareVisit from '../../../hooks/useDaycareVisit'
import OptionsMenu from '../../Common/OptionsMenu/OptionsMenu'
import SectionFormContainer from '../components/SectionFormContainer'
import InfoDialog from '../components/InfoDialog'
import SubOptionsDialog from '../components/SubOptionsDialog'
import { capitilizeMultipleWords, formatNumsToDollars } from '../../../utils'
import DaycareWizardContext from './DaycareWizardContext'
import { Checkbox, makeStyles } from '@material-ui/core'
import { FONT_WEIGHTS } from '../../../utils'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import { showError } from '../../../actions/misc/misc.actions'
import { useDispatch } from 'react-redux'

const DEFAULT_POPUP_CONTENT = { title: '', description: '' }
const DEFAULT_SUBOPTIONS_CONTENT = { title: '', description: '', options: [] }

const useStyles = makeStyles(() => ({
	checkBoxContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '.5rem',
		marginTop: '1rem',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& .MuiCheckbox-root': {
			padding: 0
		}
	}
}))

const AddOnSelection = (props) => {
	console.log('ADDON PROPS...', props)

	const classes = useStyles()
	const dispatch = useDispatch()
	const { isVipOrPetCareOnDate } = useSelectedHousehold()
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(DaycareWizardContext)
	const {
		attr,
		subtext = '',
		name,
		_id: pet_id,
		petNames,
		copy_to_all_attr,
		first_view_of_multiple = false,
		welcome_day_pets = []
	} = props
	const { loading, facility } = useDaycareVisit()

	const [infoPopupOpen, setInfoPopupOpen] = useState(false)
	const [subOptionsDialogOpen, setSubOptionsDialogOpen] = useState(false)
	const [currentPopupContent, setCurrentPopupContent] = useState(DEFAULT_POPUP_CONTENT)
	const [currentSubOptions, setCurrentSubOptions] = useState(DEFAULT_SUBOPTIONS_CONTENT)

	const title = useMemo(
		() => `Enrichments for ${wizard_state[copy_to_all_attr] === true ? petNames : name}`,
		[name, wizard_state, copy_to_all_attr]
	)

	const visitDate = useMemo(() => {
		const { dates } = wizard_state
		const [apptDate] = dates
		return apptDate
	}, [wizard_state])

	const showCopyToAllCheckbox = useMemo(() => {
		return (
			first_view_of_multiple &&
			wizard_state['pets'].length > 1 &&
			copy_to_all_attr &&
			copy_to_all_attr > '' &&
			!welcome_day_pets.length
		)
	}, [first_view_of_multiple, wizard_state, copy_to_all_attr])

	const handleCopyToAll = useCallback(
		(e) => {
			if (e.target && e.target.checked !== undefined) {
				const newValues = { ...wizard_state }
				newValues[copy_to_all_attr] = e.target.checked
				setWizardAttributeAndValues(newValues)
			}
		},
		[wizard_state, setWizardAttributeAndValues, attr, copy_to_all_attr]
	)

	const handleSelection = useCallback(
		(addon) => {
			const isVipOnVisitDate = isVipOrPetCareOnDate(visitDate, facility?._id).vip

			if (addon.vip_only && !isVipOnVisitDate) {
				dispatch(showError('This enrichment is available for VIP members only.'))
				return
			}

			const addonToAdd = addon.vip_only && isVipOnVisitDate ? { ...addon, price: 0 } : addon
			const newValues = { ...wizard_state }
			let newAddOnArray = []
			const { name } = addonToAdd
			const hasAddon = wizard_state[attr].findIndex((a) => a.name === name && a.pet_id === pet_id)
			if (hasAddon > -1) {
				newAddOnArray = newValues[attr].filter((a) => {
					if (a.category === 'meal') {
						return true
					}
					if (a.pet_id !== pet_id) {
						return true
					}
					return a.name !== name && a.pet_id === pet_id
				})
			} else {
				newAddOnArray = [...newValues[attr], { ...addonToAdd, time: 'midday', pet_id, date: visitDate }]
			}
			newValues[attr] = newAddOnArray
			setWizardAttributeAndValues(newValues)
		},
		[wizard_state, setWizardAttributeAndValues, attr, pet_id, visitDate, isVipOrPetCareOnDate, dispatch]
	)

	const handleInfoPopupClose = useCallback(() => {
		setInfoPopupOpen(false)
		setTimeout(() => {
			setCurrentPopupContent(DEFAULT_POPUP_CONTENT)
		}, 400)
	}, [setInfoPopupOpen, setCurrentPopupContent])

	const handleSubOptionsDialogClose = useCallback(() => {
		setSubOptionsDialogOpen(false)
		setTimeout(() => {
			setCurrentSubOptions(DEFAULT_SUBOPTIONS_CONTENT)
		}, 400)
	}, [setCurrentSubOptions, setSubOptionsDialogOpen])

	const handleSubOptionSelection = useCallback(
		({ addon_choices, parentAddOn }) => {
			setSubOptionsDialogOpen(true)

			const {
				price: basePrice,
				name: parentAddOnName,
				description: parentAddOnDescription,
				addon_choice_id
			} = parentAddOn

			const addOnOptions = addon_choices.map((addon, i) => {
				const { name, upcharge, id } = addon
				const cost = basePrice + upcharge
				const option = {
					title: capitilizeMultipleWords(name, ' '),
					description: formatNumsToDollars(cost),
					id: i,
					selected: addon_choice_id === id,
					useTemporarySelectionVisualFeedback: true,
					onSelected: () => {
						handleSelection({ ...parentAddOn, price: cost, addon_choice_id: id })
						handleSubOptionsDialogClose()
					}
				}
				return option
			})

			setCurrentSubOptions({
				title: parentAddOnName,
				description: parentAddOnDescription,
				options: addOnOptions
			})
		},
		[handleSelection, handleSubOptionsDialogClose, setSubOptionsDialogOpen, setCurrentSubOptions]
	)

	const handleInfoButtonClicked = useCallback(
		(content) => {
			setInfoPopupOpen(true)
			setCurrentPopupContent(content)
		},
		[setInfoPopupOpen]
	)

	const addOnCategories = useMemo(() => {
		if (!facility || !facility.addons_categories) {
			return []
		}
		return facility.addons_categories.filter(
			({ grooming_service, should_launch_grooming_booking }) =>
				!grooming_service && !should_launch_grooming_booking
		)
	}, [facility])

	const addOnServices = useMemo(() => {
		if (!facility || !facility.add_on_services) {
			return []
		}

		const { dates } = wizard_state
		const [apptDate] = dates

		const services = []

		addOnCategories.forEach(({ _id: categoryId }) => {
			const categoryServices = facility.add_on_services[categoryId] || []

			for (const service of categoryServices) {
				const {
					available_daycare,
					active,
					access,
					category,
					daycare_time_blocks,
					blocked_dates = []
				} = service
				if (!available_daycare || !active || access === 'Admin Only') {
					continue
				}

				if (blocked_dates.includes(apptDate)) {
					continue
				}

				if (category === 'schedule' && !daycare_time_blocks && !daycare_time_blocks.length) {
					continue
				}

				services.push(service)
			}
		})
		return services
	}, [addOnCategories, facility, wizard_state])

	const options = useMemo(() => {
		if (!facility) {
			return []
		}

		const opts = []

		addOnServices.forEach((addon, i) => {
			console.log('ADDON...', addon)
			const {
				name,
				description,
				addon_choices = [],
				price,
				min_price,
				max_price,
				training_service = false,
				vip_only = false
			} = addon
			const hasSuboptions = addon_choices.length > 0
			const selected = wizard_state[attr].find((addon) => addon.name === name && addon.pet_id === pet_id)
			const title = capitilizeMultipleWords(name, ' ')
			const isVipOnVisitDate = isVipOrPetCareOnDate(visitDate, facility?._id).vip
			const vipUnavailable = vip_only && !isVipOnVisitDate

			let descriptionFormatted = vip_only && isVipOnVisitDate ? 'Included' : formatNumsToDollars(price)

			if (max_price > min_price) {
				descriptionFormatted = `${formatNumsToDollars(min_price)} - ${formatNumsToDollars(max_price)}`
			}

			// if suboptions, show base and highest price, if there is a higher price than the base.
			if (hasSuboptions) {
				let largestAmount = price
				addon_choices.forEach(({ upcharge }) => {
					const total = price + upcharge
					if (total > largestAmount) {
						largestAmount = total
					}
				})
				if (largestAmount > price) {
					descriptionFormatted += ` - ${formatNumsToDollars(largestAmount)} `
				}
			}

			// if option is selected and has a suboption selected, then show the suboption name and price
			if (selected && selected.addon_choice_id && selected.addon_choice_id > '') {
				const suboption = addon_choices.find(({ id }) => id === selected.addon_choice_id)
				if (suboption) {
					descriptionFormatted = `${suboption.name} - ${formatNumsToDollars(price + suboption.upcharge)}`
				}
			}

			const option = {
				title,
				description: descriptionFormatted,
				id: i,
				disabled: loading || vipUnavailable,
				selected: selected,
				training_service,
				onSelected: () => {
					if (vipUnavailable) {
						return
					}
					if (!hasSuboptions || selected) {
						handleSelection(addon)
					} else {
						handleSubOptionSelection({ addon_choices, parentAddOn: addon })
					}
				},
				onInfoButtonClicked: () => handleInfoButtonClicked({ title, description })
			}
			opts.push(option)
		})

		return opts
	}, [
		facility,
		wizard_state,
		attr,
		handleSelection,
		handleInfoButtonClicked,
		loading,
		addOnCategories,
		addOnServices,
		pet_id,
		isVipOrPetCareOnDate,
		visitDate
	])

	return (
		<>
			<InfoDialog
				onClose={handleInfoPopupClose}
				open={infoPopupOpen}
				title={currentPopupContent.title}
				description={currentPopupContent.description}
			/>
			<SubOptionsDialog
				title={currentSubOptions.title}
				description={currentSubOptions.description}
				onClose={handleSubOptionsDialogClose}
				open={subOptionsDialogOpen}
				numberOfOptions={currentSubOptions.options?.length}>
				<OptionsMenu options={currentSubOptions.options} />
			</SubOptionsDialog>
			<SectionFormContainer title={title} subtext={subtext}>
				<OptionsMenu options={options} />
				{showCopyToAllCheckbox ? (
					<div className={classes.checkBoxContainer}>
						<Checkbox onChange={handleCopyToAll} checked={wizard_state[copy_to_all_attr] === true} />
						<div>Copy schedule for all dogs</div>
					</div>
				) : null}
			</SectionFormContainer>
		</>
	)
}

export default AddOnSelection
